import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from "react-redux";
import styles from './Create.module.scss';
import classNames from "classnames/bind";
import { getCategory, createSubject, updateSubject } from '../../../redux/apiRequest';
import { showToast } from "../../../Components/ToastMessage";

const cx = classNames.bind(styles);

function CreateNew({
    onClose,
    categoryId = '',
    editingSubject = null,
    categories = [],
    getAllSubjectByAllCategory,
    user,
    axiosJWT,
}) {
    const dispatch = useDispatch();
    const isEdit = Boolean(editingSubject);

    const [categoryType, setCategoryType] = useState(String(categoryId || ''));
    const [subjectName, setSubjectName] = useState('');
    const [formErrors, setFormErrors] = useState({ category: '', name: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const normalizedCategories = useMemo(() => {
        return categories.map((c) => {
            const normId = String(c.category_id ?? c.id ?? c._id ?? '');
            const normName = c.categoryName ?? c.name ?? '';
            return { id: normId, name: normName };
        });
    }, [categories]);

    useEffect(() => {
        if (isEdit && editingSubject) {
            setCategoryType(String(categoryId || ''));
            setSubjectName(editingSubject.subjectName ?? editingSubject.name ?? '');
        } else {
            setCategoryType(String(categoryId || ''));
            setSubjectName('');
        }
    }, [isEdit, editingSubject, categoryId]);

    useEffect(() => {
        if (user?.accessToken) {
            getCategory(user.accessToken, dispatch, axiosJWT);
        }
    }, [user?.accessToken, dispatch, axiosJWT]);

    const validateForm = () => {
        const errors = { category: '', name: '' };
        let isValid = true;

        if (!categoryType) {
            errors.category = 'Loại chuyên đề không được để trống.';
            isValid = false;
        }
        if (!subjectName.trim()) {
            errors.name = 'Tên không được để trống.';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const handleSuccess = async () => {
        if (isEdit) {
            showToast({
                type: "success",
                title: "Thành công!",
                message: "Bạn đã sửa chuyên đề thành công.",
                duration: 3000
            })
        } else {
            showToast({
                type: "success",
                title: "Thành công!",
                message: "Bạn đã thêm chuyên đề thành công.",
                duration: 3000
            })
        }
        await getAllSubjectByAllCategory(user?.accessToken, dispatch, axiosJWT);
        setIsSubmitting(false);
        onClose?.();
    };

    const handleError = () => {
        if (isEdit) {
            showToast({
                type: "error",
                title: "Thất bại!",
                message: "Vui lòng thử lại sau.",
                duration: 3000
            })
        } else {
            showToast({
                type: "error",
                title: "Thất bại!",
                message: "Vui lòng thử lại sau.",
                duration: 3000
            })
        }
        setIsSubmitting(false);
    }

    const handleSubmit = async () => {
        if (isSubmitting) return;
        if (!validateForm()) return;

        setIsSubmitting(true);
        const trimmedName = subjectName.trim();

        if (isEdit) {
            await updateSubject(
                dispatch,
                user?.accessToken,
                editingSubject.subject_id ?? editingSubject.id ?? editingSubject._id,
                { name: trimmedName, category_id: categoryType },
                axiosJWT,
                handleSuccess,
                handleError,
            );
            return;
        }

        const newSubject = { category_id: categoryType, name: trimmedName };
        await createSubject(dispatch, user?.accessToken, newSubject, axiosJWT, handleSuccess, handleError);
    };

    return (
        <div className={cx('container')} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className={cx('popup')} onClick={(e) => e.stopPropagation()}>
                <div className={cx('header')}>
                    {isEdit ? 'Chỉnh sửa chuyên đề' : 'Thêm chuyên đề mới'}
                </div>

                <div className={cx('form-group')}>
                    <label>Phân loại chuyên đề</label>
                    <select
                        value={categoryType}
                        onChange={(e) => setCategoryType(e.target.value)}
                        disabled={!isEdit && Boolean(categoryId)}
                    >
                        <option value="">Chọn phân loại</option>
                        {normalizedCategories.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    {formErrors.category && <div className={cx('error-message')}>{formErrors.category}</div>}
                </div>

                <div className={cx('form-group')}>
                    <label>Tên chuyên đề</label>
                    <input
                        type="text"
                        value={subjectName}
                        onChange={(e) => setSubjectName(e.target.value)}
                        placeholder="Nhập tên chuyên đề"
                    />
                    {formErrors.name && <div className={cx('error-message')}>{formErrors.name}</div>}
                </div>

                <div className={cx('actions')}>
                    <button className={cx('add')} onClick={handleSubmit} disabled={isSubmitting}>
                        {isEdit ? 'Lưu thay đổi' : 'Thêm'}
                    </button>
                    <button className={cx('cancel')} onClick={() => onClose?.()} disabled={isSubmitting}>
                        Hủy
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateNew;
